import { useCallback, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { TrainingMembership } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

import { useCertificateDownload } from './useCertificateDownload'

export const ExportCertificates = ({
  onClose,
  trainingMemberships,
  selectedParticipantIds = [],
}: {
  onClose: () => void
  trainingMemberships: TrainingMembership[]
  selectedParticipantIds?: number[]
}) => {
  const allParticipantEmployeeIds = useMemo(
    () =>
      trainingMemberships
        .map((membership) => membership.employee?.id)
        .filter((id): id is number => id !== undefined),
    [trainingMemberships]
  )

  const [selectedIds, setSelectedIds] = useState<number[]>(selectedParticipantIds)

  const { downloadCertificates, isDownloading, hasNoCertificates, hasError } =
    useCertificateDownload(trainingMemberships)

  const handleSelectionChange = useCallback((newSelection: EmployeeSelectorV2MultiSelectState) => {
    if (newSelection.type === 'manual') {
      setSelectedIds(newSelection.ids)
    }
  }, [])

  const handleExport = useCallback(async () => {
    await downloadCertificates(selectedIds)
  }, [selectedIds, downloadCertificates])

  const hasSelection = selectedIds.length > 0

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.export_certificates'
      )}
      primaryAction={{
        label: i18n.t('common.export'),
        onClick: handleExport,
        disabled: isDownloading || !hasSelection,
        loading: isDownloading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s32">
        <EmployeeSelectorV2
          multiple
          value={selectedParticipantIds}
          onChange={handleSelectionChange}
          forcedFilters={{
            employeeIds: allParticipantEmployeeIds,
          }}
          label={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.select_participants'
          )}
          mandatory
          options={{
            disableSelectAll: true,
          }}
        />
        {hasNoCertificates && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download'
            )}
            variant="warning"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download_description'
            )}
          />
        )}
        {hasError && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error'
            )}
            variant="critical"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error_description'
            )}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
